import type { ILog } from '@monofe/util';
import type { IServiceWorkerController } from '@monofe/worker';
import type { IDataResolver } from '../interface';
import { IMediaType } from '../interface';
import type { IModuleState } from '../state/interface';
import storageQuota from '../util/quota';
import type { IModuleDialog } from './dialog';
import media from './media';
import type { IModuleSession } from './session';
import type { IModuleToast } from './toast';
import { Message } from './toast';

const _hideViews = (deps: { store: IModuleState; dialog: IModuleDialog }) => {
    deps.dialog.hide();
    deps.store.action.hideProgressBar();
    deps.store.action.setProgress(0);
};

const _showProgress = (deps: { store: IModuleState; dialog: IModuleDialog }) => {
    deps.store.action.setProgress(0);
    deps.store.action.showProgressBar();
};

const onAccept = async (deps: {
    session: IModuleSession;
    toast: IModuleToast;
    store: IModuleState;
    dialog: IModuleDialog;
    serviceWorker: IServiceWorkerController;
    resolver: IDataResolver;
    log: ILog;
}) => {
    _hideViews(deps);

    const state = deps.store.getState();
    const { languageId, tourId } = state;
    if (!languageId || !tourId || languageId < 1 || tourId < 1) {
        return;
    }

    if (!navigator.onLine) {
        deps.toast.show(Message.DownloadFailed_Offline);
        return;
    }

    const downloadList = await deps.resolver.downloadList();
    const requiredSize = await media.downloadSize(downloadList);

    const availableSpace = await storageQuota().catch(error => Number.MAX_SAFE_INTEGER);

    if (requiredSize > availableSpace) {
        deps.toast.show(Message.DownloadFailed_NotEnoughSpace);
        return;
    }

    _showProgress(deps);

    const downloadURLs = (
        await Promise.all(
            downloadList.map(medium => deps.resolver.urlsForMedium(medium, [IMediaType.Download])),
        )
    ).flat();

    const downloadRequests = downloadURLs.map(url => ({ url: url.toString() }));

    const promise = deps.serviceWorker.active()?.addRequestsToCache(downloadRequests);

    if (promise) {
        promise.onProgress(progress => {
            const newBarProgress = Math.round(progress * 100) / 100; /* 2dp accuracy */
            deps.store.action.setProgress(newBarProgress);
        });

        await promise
            .then(() => {
                deps.toast.show(Message.DownloadCompleted);
            })
            .catch(() => {
                deps.toast.show(Message.DownloadFailed_UnexpectedError);
            });
    } else {
        deps.log.error(
            `Failed to send requests to service worker, worker:${JSON.stringify(
                deps.serviceWorker,
            )} active:${JSON.stringify(deps.serviceWorker.active())}`,
        );
        deps.toast.show(Message.DownloadFailed_UnexpectedError, ['Service Worker not running']);
    }

    _hideViews(deps);
};

export default onAccept;
